import React from 'react';

/** استایل یکنواخت برای تب «اطلاعات پایه» (دوره جدید و ویرایش) — قابل استفاده در زیربخش‌های سفارشی */
export const coreFieldClasses = {
  label: 'block text-sm font-semibold text-slate-700 mb-1.5',
  input:
    'w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/20 focus:border-[#1C3B9B] transition-colors',
  select:
    'w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/20 focus:border-[#1C3B9B] transition-colors',
  textarea:
    'w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm shadow-sm placeholder:text-slate-400 resize-y min-h-[4.5rem] focus:outline-none focus:ring-2 focus:ring-[#1C3B9B]/20 focus:border-[#1C3B9B] transition-colors',
  fileInput:
    'block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[#1C3B9B] file:text-white hover:file:bg-[#153088] file:cursor-pointer cursor-pointer rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-2 py-2',
  counter: 'text-xs text-slate-500 mt-1',
};

const accentBar: Record<string, string> = {
  brand: 'bg-[#1C3B9B]',
  slate: 'bg-slate-500',
  violet: 'bg-violet-600',
  amber: 'bg-amber-500',
  teal: 'bg-teal-600',
};

type CoreSectionProps = {
  title: string;
  subtitle?: string;
  accent?: keyof typeof accentBar;
  children: React.ReactNode;
  className?: string;
};

export const CoreSection: React.FC<CoreSectionProps> = ({
  title,
  subtitle,
  accent = 'brand',
  children,
  className = '',
}) => (
  <section
    className={`rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-100/80 ${className}`}
  >
    <header className="flex items-start gap-3 pb-4 mb-1 border-b border-slate-100">
      <span className={`mt-1 h-10 w-1 shrink-0 rounded-full ${accentBar[accent]}`} aria-hidden />
      <div className="min-w-0 flex-1 text-right">
        <h3 className="text-base font-bold text-slate-900 tracking-tight">{title}</h3>
        {subtitle ? <p className="text-xs text-slate-500 mt-1 leading-relaxed">{subtitle}</p> : null}
      </div>
    </header>
    <div className="space-y-4 pt-4">{children}</div>
  </section>
);

export const CoreFormRibbon: React.FC<{ mode: 'create' | 'edit' }> = ({ mode }) => (
  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-[#f4f7ff] to-white px-4 py-3 text-right shadow-sm">
    <p className="text-xs text-slate-700 leading-relaxed">
      <span className="font-bold text-[#1C3B9B]">
        {mode === 'create' ? 'مسیر پیشنهادی' : 'خلاصهٔ همین تب'}
      </span>
      {' — '}
      {mode === 'create'
        ? 'هویت، قیمت، مشخصات سریع و فایل‌های پایه را اینجا کامل کنید؛ متن بلند صفحهٔ عمومی دوره در تب «درباره» است؛ فصل و جلسهٔ ساختاری بعد از ذخیره با «سرفصل‌ها» یا «ویرایش».'
        : 'عنوان، قیمت، انتشار، فایل‌های اصلی و اپیزودها را اینجا به‌روز کنید؛ ویرایشگر صفحهٔ دوره در تب «درباره» و محتوای درسی در تب «سرفصل‌ها» است.'}
    </p>
  </div>
);

export type CourseQuickSpecValues = {
  courseLevel: string;
  suggestedDailyTime: string;
  teachingFormat: string;
  finalOutcomeLabel: string;
};

/** آرایهٔ توصیفی فیلدهای ویژگی سریع؛ برای افزودن فیلدهای جدید هم‌ارز API اینجا را گسترش دهید و `CourseQuickSpecValues` را به‌روز کنید. */
export const courseCoreQuickSpecFieldDefs: Array<{
  key: keyof CourseQuickSpecValues;
  label: string;
  placeholder: string;
  hint?: string;
}> = [
  { key: 'courseLevel', label: 'سطح دوره', placeholder: 'مثال: مقدماتی، میانی، پیشرفته' },
  {
    key: 'suggestedDailyTime',
    label: 'مدت پیشنهادی روزانه',
    placeholder: 'مثال: ۳۰ دقیقه تا ۱ ساعت',
  },
  { key: 'teachingFormat', label: 'نوع آموزش', placeholder: 'مثال: ضبط‌شده، آنلاین، ترکیبی' },
  {
    key: 'finalOutcomeLabel',
    label: 'خروجی نهایی (برچسب کوتاه)',
    placeholder: 'مثال: آماده ورود به پروژه واقعی',
    hint: 'کنار خلاصه و کارت‌ها نمایش داده می‌شود؛ جزئیات بیشتر در تب «درباره دوره».',
  },
];

export const CourseCoreQuickSpecFields: React.FC<{
  values: CourseQuickSpecValues;
  onChange: (next: CourseQuickSpecValues) => void;
}> = ({ values, onChange }) => {
  const patch = (key: keyof CourseQuickSpecValues, value: string) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {courseCoreQuickSpecFieldDefs.map(({ key, label, placeholder, hint }) => (
        <div key={key} className={key === 'finalOutcomeLabel' ? 'sm:col-span-2' : undefined}>
          <label className={coreFieldClasses.label}>{label}</label>
          {hint ? <p className="text-xs text-slate-500 mb-1.5 leading-relaxed">{hint}</p> : null}
          <input
            type="text"
            value={values[key]}
            onChange={(e) => patch(key, e.target.value)}
            placeholder={placeholder}
            className={coreFieldClasses.input}
          />
        </div>
      ))}
    </div>
  );
};

export const CourseCorePublishToggles: React.FC<{
  published: boolean;
  showOnHomepage: boolean;
  allowComments: boolean;
  onChange: (field: 'published' | 'showOnHomepage' | 'allowComments', value: boolean) => void;
  idPrefix: string;
}> = ({ published, showOnHomepage, allowComments, onChange, idPrefix }) => {
  const rows: Array<{
    field: 'published' | 'showOnHomepage' | 'allowComments';
    label: string;
    description: string;
  }> = [
    { field: 'published', label: 'منتشر شده', description: 'دوره در فروشگاه و لیست دوره‌ها دیده شود.' },
    { field: 'showOnHomepage', label: 'نمایش در صفحه اصلی', description: 'در بلوک‌های معرفی خانه ظاهر شود.' },
    { field: 'allowComments', label: 'ثبت نظر', description: 'کاربران برای این دوره دیدگاه ثبت کنند.' },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-1 lg:grid-cols-3">
      {rows.map(({ field, label, description }) => {
        const id = `${idPrefix}-${field}`;
        const checked =
          field === 'published'
            ? published
            : field === 'showOnHomepage'
              ? showOnHomepage
              : allowComments;
        return (
          <label
            key={field}
            htmlFor={id}
            className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 transition-colors hover:bg-slate-50/90 focus-within:ring-2 focus-within:ring-[#1C3B9B]/20"
          >
            <input
              id={id}
              type="checkbox"
              checked={checked}
              onChange={(e) => onChange(field, e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#1C3B9B] focus:ring-[#1C3B9B]"
            />
            <span className="min-w-0">
              <span className="block text-sm font-bold text-slate-900">{label}</span>
              <span className="block text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
};

/** تیک «دورهٔ رایگان» در بخش فروش — کاروسل رایگان و قیمت با سرور هماهنگ می‌شود. */
export const CourseCoreFreeCourseToggle: React.FC<{
  isFreeCourse: boolean;
  onChange: (value: boolean) => void;
  idPrefix: string;
}> = ({ isFreeCourse, onChange, idPrefix }) => {
  const id = `${idPrefix}-is-free-course`;
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-xl border border-teal-200/90 bg-teal-50/60 p-3.5 transition-colors hover:bg-teal-50/90 focus-within:ring-2 focus-within:ring-[#1C3B9B]/20"
    >
      <input
        id={id}
        type="checkbox"
        checked={isFreeCourse}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#1C3B9B] focus:ring-[#1C3B9B]"
      />
      <span className="min-w-0">
        <span className="block text-sm font-bold text-slate-900">این دوره رایگان است</span>
        <span className="block text-xs text-slate-600 mt-0.5 leading-relaxed">
          قیمت و تخفیف صفر می‌شود؛ کاربر با ثبت‌نام بدون پرداخت دسترسی می‌گیرد. برای نمایش در کاروسل «دوره‌های رایگان» خانه، وضعیت دوره را{' '}
          <span className="font-semibold text-slate-700">منتشر شده</span> کنید؛ پیش‌نویس‌ها در آن کاروسل نیستند.
        </span>
      </span>
    </label>
  );
};

export const CourseCoreHomepageCarouselToggles: React.FC<{
  featuredPopularCarousel: boolean;
  featuredBestsellerCarousel: boolean;
  featuredNewestCarousel: boolean;
  onChange: (
    field: 'featuredPopularCarousel' | 'featuredBestsellerCarousel' | 'featuredNewestCarousel',
    value: boolean,
  ) => void;
  idPrefix: string;
  /** برای دورهٔ رایگان خاموش؛ کاروسل پرفروش فقط برای دورهٔ پولی */
  includeBestsellerCarousel?: boolean;
}> = ({
  featuredPopularCarousel,
  featuredBestsellerCarousel,
  featuredNewestCarousel,
  onChange,
  idPrefix,
  includeBestsellerCarousel = true,
}) => {
  const rows: Array<{
    field: 'featuredPopularCarousel' | 'featuredBestsellerCarousel' | 'featuredNewestCarousel';
    label: string;
    description: string;
  }> = [
    {
      field: 'featuredNewestCarousel',
      label: 'کاروسل جدیدترین‌ها',
      description:
        'دوره در نوار اسلایدر «جدیدترین‌ها» صفحهٔ اصلی دیده شود؛ ترتیب بر اساس تاریخ انتشار.',
    },
    {
      field: 'featuredPopularCarousel',
      label: 'کاروسل پرطرفدارترین‌ها',
      description:
        'دوره در نوار اسلایدر «پرطرفدارترین‌ها» صفحهٔ اصلی دیده شود؛ تب دسته‌ها از دستهٔ همین دوره ساخته می‌شود.',
    },
    ...(includeBestsellerCarousel
      ? ([
          {
            field: 'featuredBestsellerCarousel' as const,
            label: 'کاروسل پرفروش‌ترین‌ها',
            description: 'فقط برای دورهٔ غیررایگان؛ ترتیب بر اساس تعداد ثبت‌نام.',
          },
        ] satisfies Array<{
          field: 'featuredPopularCarousel' | 'featuredBestsellerCarousel' | 'featuredNewestCarousel';
          label: string;
          description: string;
        }>)
      : []),
  ];

  return (
    <div
      className={`grid gap-3 sm:grid-cols-1 ${includeBestsellerCarousel ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}
    >
      {rows.map(({ field, label, description }) => {
        const id = `${idPrefix}-${field}`;
        const checked =
          field === 'featuredPopularCarousel'
            ? featuredPopularCarousel
            : field === 'featuredBestsellerCarousel'
              ? featuredBestsellerCarousel
              : featuredNewestCarousel;
        return (
          <label
            key={field}
            htmlFor={id}
            className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 transition-colors hover:bg-slate-50/90 focus-within:ring-2 focus-within:ring-[#1C3B9B]/20"
          >
            <input
              id={id}
              type="checkbox"
              checked={checked}
              onChange={(e) => onChange(field, e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#1C3B9B] focus:ring-[#1C3B9B]"
            />
            <span className="min-w-0">
              <span className="block text-sm font-bold text-slate-900">{label}</span>
              <span className="block text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
};
